import { DestroyRef, computed, inject } from '@angular/core';
import {
    patchState,
    signalStore,
    withComputed,
    withHooks,
    withMethods,
    withProps,
    withState,
} from '@ngrx/signals';
import { FirebaseError } from 'firebase/app';
import {
    type Auth,
    type MultiFactorError,
    type MultiFactorResolver,
    OAuthProvider,
    TotpMultiFactorGenerator,
    browserLocalPersistence,
    getMultiFactorResolver,
    onAuthStateChanged,
    signInWithPopup,
    signOut,
} from 'firebase/auth';
import { environment } from '../../environments/environment';
import type { LoginProvider } from '../../environments/environment.model';
import { FIREBASE_AUTH, FIREBASE_CONFIGURED } from './firebase';

export interface AuthUser {
    readonly uid: string;
    readonly email: string | null;
    readonly displayName: string | null;
}

interface AuthState {
    user: AuthUser | null;
    initialized: boolean;
    pending: boolean;
    error: string | null;
    requiresTotp: boolean;
}

const initialState: AuthState = {
    user: null,
    initialized: false,
    pending: false,
    error: null,
    requiresTotp: false,
};

const SILENT_ERRORS = new Set(['auth/popup-closed-by-user', 'auth/cancelled-popup-request']);

const ERROR_MESSAGES: Record<string, string> = {
    'auth/popup-blocked': 'The login window was blocked by the browser. Please allow popups.',
    'auth/unauthorized-domain':
        'This domain is not an authorized domain in the Firebase project.',
    'auth/operation-not-allowed': 'The login provider is not enabled for this tenant.',
    'auth/invalid-credential': 'The sign-in was rejected.',
    'auth/user-disabled': 'This user is disabled.',
    'auth/invalid-verification-code': 'The code is invalid.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/network-request-failed': 'Could not reach Firebase.',
};

function toMessage(error: unknown): string {
    if (error instanceof FirebaseError) return ERROR_MESSAGES[error.code] ?? error.message;
    return error instanceof Error ? error.message : String(error);
}

function isMfaRequired(error: unknown): error is MultiFactorError {
    return error instanceof FirebaseError && error.code === 'auth/multi-factor-auth-required';
}

function requireAuth(auth: Auth | null): Auth {
    if (!auth) {
        throw new Error(
            `Firebase is not configured for "${environment.name}" (apiKey/appId missing in environment).`,
        );
    }
    return auth;
}

export const AuthStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withProps(() => ({
        _auth: inject(FIREBASE_AUTH),
        _mfa: { resolver: null as MultiFactorResolver | null },
    })),
    withComputed(({ user }) => ({
        configured: computed(() => FIREBASE_CONFIGURED),
        loggedIn: computed(() => user() !== null),
    })),
    withMethods((store) => {
        const signIn = async (attempt: (auth: Auth) => Promise<unknown>): Promise<boolean> => {
            patchState(store, { pending: true, error: null, requiresTotp: false });
            try {
                const auth = requireAuth(store._auth);
                auth.tenantId = environment.tenantId || null;
                await auth.setPersistence(browserLocalPersistence);
                await attempt(auth);
                return true;
            } catch (error) {
                if (isMfaRequired(error)) {
                    try {
                        store._mfa.resolver = getMultiFactorResolver(requireAuth(store._auth), error);
                        patchState(store, { requiresTotp: true });
                    } catch {
                        patchState(store, {
                            error: 'The second factor could not be resolved.',
                        });
                    }
                    return false;
                }
                const silent = error instanceof FirebaseError && SILENT_ERRORS.has(error.code);
                patchState(store, { error: silent ? null : toMessage(error) });
                return false;
            } finally {
                patchState(store, { pending: false });
            }
        };

        return {
            loginWithProvider(provider: LoginProvider = environment.loginProvider): Promise<boolean> {
                return signIn((auth) => signInWithPopup(auth, new OAuthProvider(provider.providerId)));
            },

            async verifyTotp(code: string): Promise<boolean> {
                const resolver = store._mfa.resolver;
                if (!resolver) {
                    patchState(store, { error: 'There is no pending sign-in.' });
                    return false;
                }
                const hint = resolver.hints.find(
                    (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
                );
                if (!hint) {
                    patchState(store, {
                        error: 'No authenticator app is enrolled for this user.',
                    });
                    return false;
                }

                patchState(store, { pending: true, error: null });
                try {
                    await resolver.resolveSignIn(
                        TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code),
                    );
                    store._mfa.resolver = null;
                    patchState(store, { requiresTotp: false });
                    return true;
                } catch (error) {
                    patchState(store, { error: toMessage(error) });
                    return false;
                } finally {
                    patchState(store, { pending: false });
                }
            },

            cancelTotp(): void {
                store._mfa.resolver = null;
                patchState(store, { requiresTotp: false, error: null });
            },

            async logout(): Promise<void> {
                store._mfa.resolver = null;
                if (store._auth) await signOut(store._auth);
                patchState(store, { user: null, requiresTotp: false, error: null });
            },

            async idToken(): Promise<string> {
                const user = requireAuth(store._auth).currentUser;
                if (!user) throw new Error('Not signed in.');
                return user.getIdToken();
            },
        };
    }),
    withHooks({
        onInit(store) {
            if (!store._auth) {
                patchState(store, { initialized: true });
                return;
            }
            const unsubscribe = onAuthStateChanged(store._auth, (user) =>
                patchState(store, {
                    user: user
                        ? { uid: user.uid, email: user.email, displayName: user.displayName }
                        : null,
                    initialized: true,
                }),
            );
            inject(DestroyRef).onDestroy(unsubscribe);
        },
    }),
);
